import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  FlowPositionItem,
  GetProcessDraftResponse,
  GetProcessLayoutResponse,
  ProcessDraftAssignment,
  ProcessDraftStep
} from '@bliks-io/contracts';
import { processDraftKeys, processLayoutKeys } from '@/api/query-keys';
import { draftEditorEventsOf, type DraftEditorEvent } from '../draftEvents';

export interface ProcessDraftCache {
  getCachedSteps: () => ProcessDraftStep[];
  getCachedDraftAssignments: () => ProcessDraftAssignment[];
  /** Canonical draft event rows mapped to editor shape (id = stableStepId). */
  getCachedEvents: () => DraftEditorEvent[];
  getCachedGeneratedPositions: () => FlowPositionItem[];
  /** Map keyed by `stableStepId` → `{x, y}` pulled from the draft layout cache. */
  getCachedStepPositions: () => Map<string, { x: number; y: number }>;
}

export function useProcessDraftCache(processId: string): ProcessDraftCache {
  const queryClient = useQueryClient();

  const getCachedSteps = useCallback((): ProcessDraftStep[] => {
    const data = queryClient.getQueryData<GetProcessDraftResponse>(
      processDraftKeys.detail(processId)
    );
    return data?.steps ?? [];
  }, [queryClient, processId]);

  const getCachedDraftAssignments = useCallback((): ProcessDraftAssignment[] => {
    const data = queryClient.getQueryData<GetProcessDraftResponse>(
      processDraftKeys.detail(processId)
    );
    return data?.draftAssignments ?? [];
  }, [queryClient, processId]);

  const getCachedEvents = useCallback((): DraftEditorEvent[] => {
    const data = queryClient.getQueryData<GetProcessDraftResponse>(
      processDraftKeys.detail(processId)
    );
    return draftEditorEventsOf(data);
  }, [queryClient, processId]);

  const getLayout = useCallback((): GetProcessLayoutResponse | undefined => {
    return queryClient.getQueryData<GetProcessLayoutResponse>(
      processLayoutKeys.detail(processId, 'draft')
    );
  }, [queryClient, processId]);

  const getCachedGeneratedPositions = useCallback((): FlowPositionItem[] => {
    const layout = getLayout();
    return (layout?.generatedNodePositions ?? []).map((g) => ({
      stepId: g.nodeId,
      x: g.x,
      y: g.y
    }));
  }, [getLayout]);

  const getCachedStepPositions = useCallback((): Map<string, { x: number; y: number }> => {
    const layout = getLayout();
    const map = new Map<string, { x: number; y: number }>();
    for (const p of layout?.stepPositions ?? []) {
      map.set(String(p.stableStepId), { x: p.x, y: p.y });
    }
    return map;
  }, [getLayout]);

  return useMemo(
    () => ({
      getCachedSteps,
      getCachedDraftAssignments,
      getCachedEvents,
      getCachedGeneratedPositions,
      getCachedStepPositions
    }),
    [
      getCachedSteps,
      getCachedDraftAssignments,
      getCachedEvents,
      getCachedGeneratedPositions,
      getCachedStepPositions
    ]
  );
}
